import { getProjectSettingsPageUrl } from "@Features/projects/utils/projectUrls";
import { defaultModulesMapper, formCategories, RESOURCE_TYPE_PROJECT, RESOURCE_TYPE_SPACE } from "@Helpers/constants";
import { t } from "i18next";
import type { NavigateFunction } from "react-router-dom";
import type { LayoutData } from "@ServerTypes/portal/portal.schema";

export const REQUIRED_PLANS = [
  { code: "CDE", name: "Common Data Environment" },
  { code: "PM", name: "Project Management" },
  { code: "DT", name: "Digital Twins" },
];

/** Where a module-card grid is rendered. */
export const MODULE_CARD_SOURCE = {
  PROJECT_SETTINGS: "PROJECT_SETTINGS",
  PORTAL_SETTINGS: "PORTAL_SETTINGS",
} as const;
export type ModuleCardSource = (typeof MODULE_CARD_SOURCE)[keyof typeof MODULE_CARD_SOURCE];

/** `settingType` route segment for the project-settings page. */
export const MODULE_SETTING_TYPE = {
  PROJECT_MODULES: "projectModules",
} as const;

export const MODULE_TAB_TYPE = {
  FIELDS: "fields",
  CUSTOM_METADATA: "customMetadata",
  NOMENCLATURE: "nomenclature",
  FORM_TEMPLATES: "formTemplates",
  FORM_FLOWS: "formFlows",
  REVIEW_FLOWS: "reviewFlows",
  MAIL_WHITELIST: "mailWhitelist",
  MAIL_CONFIGURATION: "mailConfiguration",
  MAIL_VISIBILITY: "mailVisibility",
  ASSET_TYPES: "assetTypes",
} as const;
export type ModuleSettingType = (typeof MODULE_TAB_TYPE)[keyof typeof MODULE_TAB_TYPE];

export const setModuleTabSearchParams = (settingType: string, resourceType: string, navigate: NavigateFunction) => {
  const params = new URLSearchParams(globalThis.location.search);
  const projectId = params.get("projectId");
  if (projectId) {
    navigate(
      getProjectSettingsPageUrl({
        projectId,
        tab: MODULE_SETTING_TYPE.PROJECT_MODULES,
        extraSearchParams: { settingType, resourceType: resourceType?.toLowerCase() },
      }),
    );
  }
};

export const customModuleIconsList: string[] = [
  "CustomModule1",
  "CustomModule2",
  "CustomModule3",
  "CustomModule4",
  "CustomModule5",
  "CustomModule6",
  "CustomModule7",
  "CustomModule8",
  "CustomModule9",
  "CustomModule10",
  "CustomModule11",
  "CustomModule12",
  "CustomModule13",
  "CustomModule14",
];

/** One clickable sub-tab rendered inside a module card. */
export type ModuleCardTab = {
  id: string;
  name: string;
  onClick?: (navigate: NavigateFunction) => void;
};

/** One default-module card. Client-side navigation mapper only — no server counterpart. */
export type ModuleCard = {
  id: string;
  name: string;
  icon: string;
  module: string;
  /**
   * Whether the module is enabled for this portal. Sourced from `layout.<MODULE>.ENABLE`,
   * which `layoutModuleEntrySchema` declares as `boolean | Record<string, unknown>`; every
   * module read here is seeded as a plain boolean (see portal.default.layout.ts), so the
   * reads below assert `boolean | undefined` rather than widen this to `unknown` — the
   * truthiness test in defaultModules.tsx is unchanged either way.
   */
  allow?: boolean;
  moduleType: string;
  resourceType: string;
  tabs?: ModuleCardTab[];
};

/** One accordion group of default-module cards. */
export type ModuleGroup = {
  name: string;
  id: string;
  modules: ModuleCard[];
};

export const getDefaultModuleMapper = (layout: LayoutData): ModuleGroup[] => {
  return [
    {
      name: t("Common Data Environment"),
      id: "commonDataEnvironment",
      modules: [
        // {
        //   id: "spaces",
        //   name: t("Spaces"),
        //   icon: "spaces",
        //   module: defaultModulesMapper.SPACES,
        //   moduleType: "CDE",
        //   allow: true,
        // },
        {
          id: "folders",
          name: t("Folders"),
          icon: "folder",
          module: defaultModulesMapper.FOLDERS,
          allow: true,
          moduleType: "CDE",
          resourceType: RESOURCE_TYPE_SPACE,
          // tabs: [
          //   {
          //     id: "fields",
          //     name: t("Fields"),
          //     onClick: (navigate: NavigateFunction) =>
          //       setModuleTabSearchParams("fields", defaultModulesMapper.FOLDERS, navigate),
          //   },
          //   {
          //     id: "customMetadata",
          //     name: t("Custom Metadata"),
          //     onClick: (navigate: NavigateFunction) =>
          //       setModuleTabSearchParams("customMetadata", defaultModulesMapper.FOLDERS, navigate),
          //   },
          //   {
          //     id: "nomenclature",
          //     name: t("Nomenclature"),
          //     onClick: (navigate: NavigateFunction) =>
          //       setModuleTabSearchParams("nomenclature", defaultModulesMapper.FOLDERS, navigate),
          //   },
          // ],
        },
        {
          id: "tasks",
          name: t("Tasks"),
          module: defaultModulesMapper.TASKS,
          icon: "TasksIcon",
          allow: layout.TASKS?.ENABLE as boolean | undefined,
          moduleType: "CDE",
          resourceType: RESOURCE_TYPE_SPACE,
          // tabs: [
          //   {
          //     id: "fields",
          //     name: t("Fields"),
          //     onClick: (navigate: NavigateFunction) => setModuleTabSearchParams("fields", defaultModulesMapper.TASKS, navigate),
          //   },
          // ],
        },
        {
          id: "issues",
          name: t("Issues"),
          icon: "issues",
          module: defaultModulesMapper.ISSUES,
          allow: layout.ISSUES?.ENABLE as boolean | undefined,
          moduleType: "CDE",
          resourceType: RESOURCE_TYPE_SPACE,
          tabs: [
            // {
            //   id: "fields",
            //   name: t("Fields"),
            //   onClick: (navigate: NavigateFunction) => setModuleTabSearchParams("fields", defaultModulesMapper.ISSUES, navigate),
            // },
            {
              id: "formTemplates",
              name: t("Form Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.issues, navigate),
            },
          ],
        },
        {
          id: "transmittals",
          name: t("Transmittals"),
          icon: "Transmittals",
          module: defaultModulesMapper.TRANSMITTALS,
          allow: layout.TRANSMITTAL?.ENABLE as boolean | undefined,
          moduleType: "CDE",
          resourceType: RESOURCE_TYPE_SPACE,
          tabs: [
            // {
            //   id: "fields",
            //   name: t("Fields"),
            //   onClick: (navigate: NavigateFunction) => setModuleTabSearchParams("fields", defaultModulesMapper.TRANSMITTALS, navigate),
            // },
            {
              id: "formTemplates",
              name: t("Form Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.transmittal, navigate),
            },
          ],
        },
        {
          id: "reviews",
          name: t("Reviews"),
          icon: "Review",
          module: defaultModulesMapper.REVIEWS,
          allow: layout.REVIEWFLOW?.ENABLE as boolean | undefined,
          moduleType: "CDE",
          resourceType: RESOURCE_TYPE_SPACE,
          tabs: [
            // {
            //   id: "fields",
            //   name: t("Fields"),
            //   onClick: (navigate: NavigateFunction) => setModuleTabSearchParams("fields", defaultModulesMapper.REVIEWS, navigate),
            // },
            {
              id: "reviewFlows",
              name: t("Review Flows"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.REVIEW_FLOWS, defaultModulesMapper.REVIEWS, navigate),
            },
          ],
        },
        {
          id: "clashTests",
          name: t("Clash Tests"),
          icon: "ClashIcon",
          module: defaultModulesMapper.CLASH,
          allow: layout.CLASH_TEST?.ENABLE as boolean | undefined,
          moduleType: "CDE",
          resourceType: RESOURCE_TYPE_SPACE,
        },
      ],
    },
    {
      name: t("Project Management"),
      id: "projectManagement",
      modules: [
        {
          id: "files",
          name: t("Files"),
          icon: "files",
          module: defaultModulesMapper.FILES,
          allow: layout.FILES?.ENABLE as boolean | undefined,
          resourceType: RESOURCE_TYPE_SPACE,
          moduleType: "PM",
          tabs: [
            // {
            //   id: "fields",
            //   name: t("Fields"),
            //   onClick: (navigate: NavigateFunction) => setModuleTabSearchParams("fields", defaultModulesMapper.FILES, navigate),
            // },
            {
              id: "formTemplates",
              name: t("Form Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.files, navigate),
            },
            {
              id: "modelTemplate",
              name: t("Model Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.model, navigate),
            },
            {
              id: "drawingTemplate",
              name: t("Drawing Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.drawing, navigate),
            },
            // {
            //   id: "customMetadata",
            //   name: t("Custom Metadata"),
            //   onClick: (navigate: NavigateFunction) => setModuleTabSearchParams("customMetadata", defaultModulesMapper.FILES, navigate),
            // },
          ],
        },
        {
          id: "mailbox",
          name: t("Mailbox"),
          icon: "Mailbox",
          module: defaultModulesMapper.MAILBOX,
          allow: layout.MAILBOX?.ENABLE as boolean | undefined,
          moduleType: "PM",
          resourceType: RESOURCE_TYPE_PROJECT,
          tabs: [
            // {
            //   id: "fields",
            //   name: t("Fields"),
            //   onClick: (navigate: NavigateFunction) => setModuleTabSearchParams("fields", defaultModulesMapper.MAILBOX, navigate),
            // },
            {
              id: "formTemplates",
              name: t("Fields"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.mailBox, navigate),
            },
            {
              id: "whitelist",
              name: t("Whitelist"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.MAIL_WHITELIST, defaultModulesMapper.MAILBOX, navigate),
            },
            {
              id: "visibility",
              name: t("Visibility"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.MAIL_VISIBILITY, defaultModulesMapper.MAILBOX, navigate),
            },
            {
              id: "configuration",
              name: t("Configuration"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.MAIL_CONFIGURATION, defaultModulesMapper.MAILBOX, navigate),
            },
          ],
        },
        {
          id: "progressUpdates",
          name: t("Progress Updates"),
          icon: "ProgressUpdates",
          module: defaultModulesMapper.PROGRESS_UPDATES,
          allow: layout.PROGRESS_UPDATES?.ENABLE as boolean | undefined,
          moduleType: "PM",
          resourceType: RESOURCE_TYPE_SPACE,
          tabs: [
            {
              id: "formTemplates",
              name: t("Form Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.progressUpdates, navigate),
            },
            {
              id: "formFlows",
              name: t("Form Flows"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_FLOWS, defaultModulesMapper.PROGRESS_UPDATES, navigate),
            },
          ],
        },
        {
          id: "inspections",
          name: t("Inspections"),
          icon: "inspectionForm",
          module: defaultModulesMapper.INSPECTION,
          allow: layout.INSPECTION?.ENABLE as boolean | undefined,
          moduleType: "PM",
          resourceType: RESOURCE_TYPE_SPACE,
          tabs: [
            {
              id: "formTemplates",
              name: t("Form Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.inspection, navigate),
            },
            {
              id: "formFlows",
              name: t("Form Flows"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_FLOWS, defaultModulesMapper.INSPECTION, navigate),
            },
          ],
        },
        {
          id: "bills",
          name: t("Bills"),
          icon: "bills",
          module: defaultModulesMapper.BILLS,
          moduleType: "PM",
          allow: layout.BILLS?.ENABLE as boolean | undefined,
          resourceType: RESOURCE_TYPE_SPACE,
          tabs: [
            {
              id: "formTemplates",
              name: t("Form Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.bills, navigate),
            },
            {
              id: "formFlows",
              name: t("Form Flows"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_FLOWS, defaultModulesMapper.BILLS, navigate),
            },
          ],
        },
        {
          id: "changeOrders",
          name: t("Change Orders"),
          icon: "changeOrder",
          module: defaultModulesMapper.CHANGE_ORDER,
          moduleType: "PM",
          allow: layout.CHANGE_ORDER?.ENABLE as boolean | undefined,
          resourceType: RESOURCE_TYPE_SPACE,
          tabs: [
            {
              id: "formTemplates",
              name: t("Form Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.changeOrder, navigate),
            },
            {
              id: "formFlows",
              name: t("Form Flows"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_FLOWS, defaultModulesMapper.CHANGE_ORDER, navigate),
            },
          ],
        },
        {
          id: "contracts",
          name: t("Contracts"),
          icon: "Contracts",
          module: defaultModulesMapper.CONTRACTS,
          allow: layout.CONTRACT?.ENABLE as boolean | undefined,
          moduleType: "PM",
          resourceType: RESOURCE_TYPE_SPACE,
          tabs: [
            {
              id: "formTemplates",
              name: t("Form Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.contract, navigate),
            },
          ],
        },
        {
          id: "schedules",
          name: t("Schedule"),
          icon: "schedules",
          module: defaultModulesMapper.SCHEDULES,
          moduleType: "PM",
          allow: layout.SCHEDULES?.ENABLE as boolean | undefined,
          resourceType: RESOURCE_TYPE_SPACE,
        },
        {
          id: "estimates",
          name: t("Estimates"),
          icon: "estimates",
          module: defaultModulesMapper.ESTIMATES,
          moduleType: "PM",
          allow: layout.ESTIMATES?.ENABLE as boolean | undefined,
          resourceType: RESOURCE_TYPE_SPACE,
        },
      ],
    },
    {
      name: t("Digital Twin"),
      id: "digitalTwin",
      modules: [
        {
          id: "assets",
          name: t("Assets"),
          icon: "Assets",
          module: defaultModulesMapper.ASSETS,
          allow: layout.ASSETS?.ENABLE as boolean | undefined,
          resourceType: RESOURCE_TYPE_SPACE,
          moduleType: "CDE",
          tabs: [
            {
              id: "formTemplates",
              name: t("Form Templates"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.FORM_TEMPLATES, formCategories.assets, navigate),
            },
            {
              id: "assetTypes",
              name: t("Asset Types"),
              onClick: (navigate: NavigateFunction) =>
                setModuleTabSearchParams(MODULE_TAB_TYPE.ASSET_TYPES, formCategories.assets, navigate),
            },
          ],
        },
      ],
    },
  ];
};
